import React, { useRef, useEffect, useState, useCallback } from 'react';
import { clipAtOffset, nextUnplayedClip } from '../util/mcpbClips';
import { buildStreamUrl } from '../util/buildStreamUrl';
import { clipDownloadUrl } from '../util/clipDownloadUrl';
import { getBackend } from '../backend';
import { deriveActionItems, ARCHIVE_FLAG, EXPORT_FLAG } from '../util/mcpbActions';
import { downloadScreenshot } from '../util/screenshotHelper';
import { useSlotVideo } from '../hooks/useSlotVideo';
import { useSlotWebCodecs } from '../hooks/useSlotWebCodecs';
import { useVideoInfo } from '../hooks/useVideoInfo';
import { useCanvasInfo } from '../hooks/useCanvasInfo';
import { useCellClock } from '../hooks/useCellClock';
import { formatVideoInfo } from '../util/mcpbVideoInfo';
import { webCodecsPlaybackSupported } from '../util/playbackCapability';
import { formatB2Timestamp } from '../player/b2Sei';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import Cell from './Cell';
import { getLiveFrame, setLiveFrame } from '../util/liveFrameCache';

// "View single" / "Show all cameras" icons (Material fullscreen / fullscreen-exit) for the ••• single-view item.
const SINGLE_VIEW_PATH = 'M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z';
const SHOW_ALL_PATH = 'M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z';

// Prefetch lead window (Java MCTimelineBar.preloadSeconds / BASE_PRELOAD_SECONDS): warm the next clip
// once the playhead is within this many seconds of its start, scaled by playback speed.
const PRELOAD_LEAD_SEC = 10;

/**
 * SlotCell — one grid slot: resolves the clip under the SHARED playhead offset for this slot's own
 * track, builds the stream URL, owns the <video> ref, and drives it via useSlotVideo. Rendering this
 * once per slot (keyed by index) is what lets the conf-grid (1/2/4) share one master clock — each cell
 * seeks its own clip to the same offset, so the pictures stay together (1g).
 *
 * The master playhead (useTimelinePlayhead in the View) now drives prev/next-clip + auto-skip over the
 * UNION of all tracks (2a); every SlotCell follows the shared offset and resolves its OWN track here,
 * rendering a per-cell gap state when this cam has no clip under the offset. Drift correction + the 5s
 * buffer-gate that make conf>1 frame-tight are the 2a-3 sync engine (feature-inventory §12) — NOT here.
 * Presentational chrome lives in Cell.
 */
const SlotCell = ({
  camera, slotClips = [], windowStartTs,
  offsetSec, playing, holding = false, speed, streamCtx = {}, loading = false,
  preferPro2 = false,
  slotIndex, onStall, onVideoEl, onCacheProgress, showVideoInfo = false, onToggleVideoInfo,
  canExport = false, flagEvent, pushToast, startDownload,
  zoomed = false, dimmed = false, canZoom = false, onZoom, onViewSingle,
  flash = false, hovered = false,
  selected = false, onSelect, actionsShown = false, alwaysShowQuickActions = false,
  compact = false,
  dragEnabled = false,
  onAddCamera, onChangeCamera, onRemoveFromView, onMediaError,
  camLabel = '',
}) => {
  // Cell drag-and-drop (dnd-kit): the cell root is BOTH a drag source (grab to carry — conf>1 + holds a
  // cam) AND a drop target. `data.type:'cell'` lets the View's onDragEnd tell a cell-swap from a pill- or
  // tree-drag (util/mcpbDrag resolveDrop). The droppable is ALWAYS enabled — a pill or tree cam can be
  // dropped onto any cell, including the single cell at conf=1 (where cell↔cell swap is off). The View
  // performs the swap/place on drop and renders the carry-the-tile ghost via DragOverlay.
  const draggable = useDraggable({ id: slotIndex, data: { type: 'cell', slot: slotIndex }, disabled: !dragEnabled || !camera });
  const droppable = useDroppable({ id: slotIndex });
  const setDndRef = (node) => { draggable.setNodeRef(node); droppable.setNodeRef(node); };
  const active = (camera && windowStartTs) ? clipAtOffset(slotClips, offsetSec, windowStartTs) : null;
  const activeClip = active ? active.clip : null;
  const hasClip = !!activeClip;
  // Dragon, local NVR, and eView NVR recordings use the WebCodecs player when supported. Cloud legacy
  // stays native because its ranged fetch still needs Bearer-header authentication. (Develop's B2
  // recorded-WebCodecs path — #86bb7phhh merge: supersedes our earlier dragon-only usesWebCodecsPlayback
  // gate now that Ryan's pbserver serves a WebCodecs-consumable MP4 for local/eview too.)
  //
  // Keyed on the TRACK (does this cam have dragon clips), NOT on the current clip — so the canvas + warm
  // player stay live while this track sits in a GAP ("No video at this time"), and its upcoming clip is
  // pre-warmed before the playhead reaches it (David 2026-07-17: gap tracks were showing a loader because
  // they didn't warm until they had an active clip).
  const mediaClips = (slotClips || []).filter(Boolean);
  const webCodecsCandidate = !!(
    camera &&
    mediaClips.length &&
    webCodecsPlaybackSupported() &&
    mediaClips.every((clip) => clip.directMedia || !clip.fCloudVS)
  );
  const [webCodecsFailedClipId, setWebCodecsFailedClipId] = useState(null);
  // Stay on the WebCodecs canvas even in a GAP (no activeClip) so the cell keeps showing that camera's
  // last decoded frame (a "still looking at this / no footage right now" preview) instead of dropping to
  // the DIVIDIA placeholder — the canvas retains its pixels across teardown. Only fall back to <video>
  // when the CURRENT clip specifically failed WebCodecs (David 2026-08-14).
  const isWebCodecs = webCodecsCandidate && (!activeClip || webCodecsFailedClipId !== activeClip.bID);

  // Cloud legacy clips (regular field fleet) are pulled from the device to cloud
  // storage ON DEMAND — the clip URL 502s until it is cached. So before playing an
  // uncached legacy clip, run the cache-then-play flow (Java requestVideoEvent +
  // getVideoEventCacheStatus poll, here CloudNvrAdapter.ensureClipCached) and hold the
  // <video> src (show a caching spinner) until it is ready. Dragon (directMedia) clips
  // and already-cached clips (fCached) skip this and play immediately; local NVR clips
  // have no fCloudVS flag so this is a no-op for them.
  const needsCache = !!(activeClip && activeClip.fCloudVS && !activeClip.directMedia && !activeClip.fCached);
  // Prefetch/warming target: the NEXT clip in THIS track, once the playhead is within the (speed-scaled)
  // lead window (port of Java MCPlayerView.preload + MCTimelineBar.preloadSeconds). Warming it during the
  // current clip's playback makes the boundary seamless (no per-clip first-frame spinner). Only while
  // playing (Java preloads during playback); never below a 10s lead even at slow speeds.
  const nextClip = (camera && windowStartTs && playing)
    ? nextUnplayedClip(slotClips, offsetSec, windowStartTs, PRELOAD_LEAD_SEC * Math.max(1, speed))
    : null;
  const nextNeedsCache = !!(nextClip && nextClip.fCloudVS && !nextClip.directMedia && !nextClip.fCached);
  const [cacheReadyIds, setCacheReadyIds] = useState({});
  const [caching, setCaching] = useState(false);
  const [cacheProgress, setCacheProgress] = useState(0);
  const [cacheError, setCacheError] = useState(null);
  const cacheToken = streamCtx.sSess; // cloud JWT from Redux (state.auth.sSess) — reliable auth source
  useEffect(() => {
    if (!activeClip || !needsCache) { setCaching(false); setCacheError(null); return undefined; }
    const bID = activeClip.bID;
    if (cacheReadyIds[bID]) { setCaching(false); return undefined; }
    let alive = true;
    setCaching(true);
    setCacheProgress(0);
    setCacheError(null);
    const be = getBackend();
    const report = (pct) => { if (onCacheProgress) onCacheProgress(bID, pct); };
    const p = (be && be.ensureClipCached)
      ? be.ensureClipCached(activeClip, {
        token: cacheToken,
        onProgress: (pct) => { if (alive) { setCacheProgress(pct); report(pct); } },
      })
      : Promise.resolve({ ok: false, error: 'Unavailable' });
    p.then((res) => {
      if (!alive) return;
      setCaching(false);
      if (res && res.ok) { setCacheReadyIds((m) => ({ ...m, [bID]: true })); report(100); }
      else setCacheError((res && res.error) || 'Could not load clip');
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeClip && activeClip.bID, needsCache, cacheToken]);

  const clipReady = !!activeClip && (!needsCache || !!cacheReadyIds[activeClip.bID]);
  // Cloud pro2/pro1 per-tile (#86bbmxrtt). The View passes preferPro2 (layout-derived). Only dragon
  // cloud clips carry a lighter twin (sMoviePro2); when we want it, play it as the PRIMARY and hand the
  // player the main clip (sMovie) as the fallback — it retries on engine_backend's fast 404 for a clip
  // with no record_pro2 twin. Non-twin clips (local/eview/cloud-legacy) keep sMovie with no fallback.
  const clipPro2Url = activeClip && activeClip.sMoviePro2;
  const wantPro2 = !!(preferPro2 && clipPro2Url);
  const activePrimaryFile = wantPro2 ? clipPro2Url : (activeClip && activeClip.sMovie);
  const activeFallbackFile = wantPro2 ? (activeClip && activeClip.sMovie) : null;
  // The profile REQUESTED for this tile — 2 for the twin, 1 for the main clip; the local pbserver path
  // keeps its own streamCtx.profile hint. The player reports the profile it ACTUALLY decodes (post-
  // fallback) to the Camera-Info overlay via getStreamInfo, so this is only the initial/requested value.
  const requestedProfile = clipPro2Url ? (wantPro2 ? 2 : 1) : (streamCtx.profile || 1);
  const videoUrl = (activeClip && clipReady)
    ? buildStreamUrl({ file: activePrimaryFile, cloud: activeClip.fCloudVS, ...streamCtx })
    : '';
  const videoFallbackUrl = (activeClip && clipReady && activeFallbackFile)
    ? buildStreamUrl({ file: activeFallbackFile, cloud: activeClip.fCloudVS, ...streamCtx })
    : null;
  // Download URL = the same clip PLUS the server OSD burn (&overlay=1&mp4=1) for local/eview, so the SAVED
  // file carries camera name + timestamp (+ POS register). The playback STREAM (videoUrl) stays overlay-free
  // — the viewer draws name/timestamp client-side. Dragon downloads its raw engine_backend MP4 (no pbserver
  // postoverlay); cloud-legacy uses its cloud URL (buildStreamUrl ignores overlay on the cloud branch).
  //
  // NOT keyed on isWebCodecs (#86bb7phhh, cs2427 2026-08-17): that used to hand back `sMovie` verbatim on
  // the WebCodecs branch, which is only correct for dragon (absolute engine_backend URL). Once the
  // WebCodecs gate widened to local/eview, local clips took that branch too and `sMovie` is a bare
  // VideoStore PATH — the browser fetched it off the viewer docroot and every Download 404'd. The player
  // and the download URL are independent concerns; clipDownloadUrl decides on the CLIP's own shape.
  const downloadUrl = clipDownloadUrl(activeClip, streamCtx, { clipReady });

  // Prefetch the NEXT legacy clip's cache pull EARLY (port of Java's cloud warm-up that runs ahead of
  // preload): start ensureClipCached while the current clip still plays and write into the SHARED
  // cacheReadyIds, so when the playhead reaches this clip `clipReady` is already true — no caching
  // spinner at the boundary. Progress feeds the timeline shading (onCacheProgress) but NEVER the
  // buffer-gate (onStall): a warming/preloading slot must not stall the shared clock (Java MCBufferGate
  // — isPreloading slots can't stall). Deduped per bID via a ref so a per-tick re-render can't re-pull.
  const prefetchingRef = useRef(null);
  useEffect(() => {
    if (!nextClip || !nextNeedsCache) return undefined;
    const bID = nextClip.bID;
    if (cacheReadyIds[bID] || prefetchingRef.current === bID) return undefined;
    prefetchingRef.current = bID;
    let alive = true;
    const be = getBackend();
    const p = (be && be.ensureClipCached)
      ? be.ensureClipCached(nextClip, {
        token: cacheToken,
        onProgress: (pct) => { if (alive && onCacheProgress) onCacheProgress(bID, pct); },
      })
      : Promise.resolve({ ok: false });
    p.then((res) => {
      if (!alive) return;
      if (prefetchingRef.current === bID) prefetchingRef.current = null;
      if (res && res.ok) setCacheReadyIds((m) => (m[bID] ? m : { ...m, [bID]: true }));
      // A prefetch failure is silent: the active-clip path retries + surfaces the error only if the
      // playhead actually reaches this clip (so a clip that ends up skipped never nags the user).
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextClip && nextClip.bID, nextNeedsCache, cacheToken]);

  // Per-track gap (2a): a camera is assigned but its track has no clip under the shared offset. Split
  // into two states (David 2026-08-04): while events are still loading with nothing yet, it's LOADING
  // (spinner); otherwise it's a genuine dead-air gap → the tile just DARKENS (the darkening is the
  // "no video available" cue; no text).
  const inGap = !!camera && !!windowStartTs && !hasClip;
  const isLoading = loading && (!slotClips || slotClips.length === 0);

  // Report this cell's clip presence to the View's buffer-gate (2a-3). A gap cell can never stall, so
  // also force its buffering flag false (its <video> has no src and won't emit recovery events).
  // Keyed on the camera too (86bc8571y): with a camera set's events prefetched, a swap lands the new
  // camera with a clip in the same render, so `hasClip` stays true across it, and the View has just
  // reset the gate map, which would leave the new camera's buffering report without `hasClip`.
  // It carries the last buffering report too: the players report only CHANGES, so a camera that was
  // still loading when the set swapped would otherwise lose its `true` to that reset, and the new
  // player's own `true` is then dropped as a repeat.
  const stallCamKey = camera ? `${camera.bSerial}_${camera.bCamera}` : '';
  const lastBufRef = useRef(false);
  useEffect(() => {
    if (!onStall) return;
    onStall(slotIndex, hasClip ? { hasClip: true, isBuffering: lastBufRef.current } : { hasClip: false, isBuffering: false });
  }, [hasClip, slotIndex, onStall, stallCamKey]);

  // Report the on-demand cache pull as "buffering" AND "caching" so the master buffer-gate
  // holds the shared clock while a legacy clip is being pulled to storage. `caching` lets
  // the gate keep holding past the normal 5s ceiling (a caching cell has no <video> buffer
  // to "grow", so the progress heuristic would otherwise release and march past it).
  useEffect(() => {
    if (onStall) onStall(slotIndex, { isBuffering: caching, caching });
  }, [caching, slotIndex, onStall]);

  // v1.5: surface this cell's rebuffering state for the spinner. Same signal that feeds the View's
  // buffer-gate (onStall) — kept locally too so the tile can paint a spinner. Display is gated on
  // hasClip below, so a gap/dead tile (no <video> src) never shows a stale spinner.
  const [isBuffering, setIsBuffering] = useState(false);
  // Sticky: has this WebCodecs cell ever painted a frame? Once true, hold the shown frame at clip
  // boundaries / re-seeks instead of flashing the loader over an already-painted cell (David: "the
  // loader means this cam has no frame yet"). Reset when the slot's camera changes.
  const [hasEverPainted, setHasEverPainted] = useState(false);
  // Live→Playback preview: show the last live canvas frame until real playback video paints.
  // Cleared once video renders so it never persists over live content.
  // Live stores frames keyed by camera.id (VideoViewWs passes camera.id as cameraId
  // to CameraStreamContainerWs, which sets data-cam-id on the canvas). Playback slots
  // may have bID, id, or just bCamera — try all so the lookup never misses.
  const camFrameKey = camera
    ? String(camera.bID || camera.id || camera.bCamera || '')
    : null;
  const [livePreview, setLivePreview] = useState(() => camFrameKey ? getLiveFrame(camFrameKey) : null);
  useEffect(() => {
    if (hasEverPainted && livePreview) {
      setLivePreview(null);
      if (camFrameKey) setLiveFrame(camFrameKey, null);
    }
  }, [hasEverPainted, livePreview, camFrameKey]);
  useEffect(() => { setLivePreview(camFrameKey ? getLiveFrame(camFrameKey) : null); }, [camFrameKey]);
  // Decode-error recovery state (task 2, David 2026-08-05): useSlotVideo drives the retry/backoff on a
  // <video> media error and reports 'recovering' (a reload is in flight → spinner, not an error) →
  // 'ok' (recovered) or 'failed' (backoff exhausted → surface "Can't play this clip" until the playhead
  // moves on). Native-<video> path only; the WebCodecs path has no <video> so it stays 'ok' here.
  const [mediaState, setMediaState] = useState('ok');

  const videoRef = useRef(null);

  useSlotVideo(videoRef, {
    clipId: activeClip ? activeClip.bID : null,
    offsetInClip: active ? active.offsetInClip : 0,
    playing, holding, speed, hasClip: hasClip && !isWebCodecs,   // WebCodecs path drives its own canvas
    onBuffering: (b) => {
      lastBufRef.current = b;
      setIsBuffering(b);
      if (onStall) onStall(slotIndex, { isBuffering: b });
    },
    onRecovery: setMediaState,
  });

  // WebCodecs streaming player for direct clips, driven by the SAME shared playhead as the
  // <video> path (offsetInClip/playing) and reporting buffering into the same buffer-gate.
  // Renders into a <canvas> handed to Cell as its media surface. No-ops for the <video> path
  // (null url → usePlaybackPlayer stays uninitialised).
  // The immediate NEXT clip in this track — the WebCodecs warm target. No lead gate: warm it for
  // the whole current clip so the promotion is decoded + ready before the boundary (Java preload).
  const wcNextClip = isWebCodecs ? nextUnplayedClip(slotClips, offsetSec, windowStartTs) : null;
  const nextWantPro2 = !!(preferPro2 && wcNextClip && wcNextClip.sMoviePro2);
  const wcNextUrl = wcNextClip
    ? buildStreamUrl({ file: nextWantPro2 ? wcNextClip.sMoviePro2 : wcNextClip.sMovie, cloud: wcNextClip.fCloudVS, ...streamCtx })
    : null;
  const wcNextFallbackUrl = (wcNextClip && nextWantPro2)
    ? buildStreamUrl({ file: wcNextClip.sMovie, cloud: wcNextClip.fCloudVS, ...streamCtx })
    : null;
  const [seiFrame, setSeiFrame] = useState(null);
  const activeClipId = activeClip ? activeClip.bID : null;
  const onMetadataUpdate = useCallback((metadata) => {
    setSeiFrame((previous) => {
      const old = previous && previous.metadata;
      if (
        previous && previous.clipId === activeClipId && old && metadata &&
        old.camname === metadata.camname && old.time === metadata.time && old.gmtoffset === metadata.gmtoffset
      ) return previous;
      return { clipId: activeClipId, metadata };
    });
  }, [activeClipId]);
	const wc = useSlotWebCodecs(
    isWebCodecs && activeClip ? videoUrl : null,
    isWebCodecs && wcNextClip ? wcNextUrl : null,
    {
      offsetInClip: active ? active.offsetInClip : 0,
      playing,
      holding,
      speed,
      hasClip: isWebCodecs && hasClip,
      // Cloud pro2 → pro1 fallback + the profile the Camera-Info overlay reports (requestedProfile above).
      fallbackUrl: isWebCodecs ? videoFallbackUrl : null,
      nextFallbackUrl: isWebCodecs ? wcNextFallbackUrl : null,
      profile: requestedProfile,
      fallbackProfile: 1,
      onBuffering: (b) => {
        lastBufRef.current = b;
        setIsBuffering(b);
        if (onStall) onStall(slotIndex, { isBuffering: b });
      },
      onMetadataUpdate,
      // showObjects/showLpr intentionally omitted → the player seeds from the shared
      // overlay prefs (Setup / in-view control) + live-subscribes. Legacy cloud carries
      // no object/LPR metadata, so the toggle is a no-op there regardless.
    },
	);

	// Once a WebCodecs cell has painted, suppress the loader for clip-boundary transitions (hold the
	// shown frame, don't flash a spinner). Resets when the camera changes.
	const wcReady = !!(wc && wc.ready);
	useEffect(() => { if (wcReady) setHasEverPainted(true); }, [wcReady]);
	const _camKey = camera ? (camera.bSerial + '|' + camera.sDeviceID) : '';
	useEffect(() => { setHasEverPainted(false); }, [_camKey]);

	// A codec or decoder failure affects only the active clip. Switch that clip to
	// the browser's native video path, then retry WebCodecs on the next clip.
	useEffect(() => {
		if (isWebCodecs && activeClip && wc.error) setWebCodecsFailedClipId(activeClip.bID);
	}, [isWebCodecs, activeClip, wc.error]);

  // Register this cell's drawable + download URL with the View (by slot index) so BULK actions
  // (Snapshot/Download all) and the drag-ghost frame-capture can reach every cell. Capability-based:
  // the dragon WebCodecs path has NO <video> — the frame lives on the active <canvas> — so register
  // that; local/eview/cloud-legacy register their <video>. `src` is the clip download URL (same shape
  // for both). Re-registers when the active canvas flips (A/B promotion) or the clip URL changes.
  useEffect(() => {
    if (!onVideoEl) return undefined;
    const el = isWebCodecs ? (wc.getActiveCanvas && wc.getActiveCanvas()) : videoRef.current;
    // Register the OSD-burned download URL (downloadUrl) so Download-all saves files with the OSD too;
    // dragon registers its raw engine_backend URL (buildStreamUrl would 404 a dragon clip).
    onVideoEl(slotIndex, el ? { el, src: downloadUrl } : null);
    return () => { if (onVideoEl) onVideoEl(slotIndex, null); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onVideoEl, slotIndex, isWebCodecs, downloadUrl, activeClip && activeClip.sMovie, wc.activeBuf]);

  // Cog "Camera Info overlay" (David 2026-06-21): when the global toggle is on, read this cell's live
  // stream stats and pass the formatted lines to Cell (top-left). Gated on hasClip so a gap/empty tile
  // shows nothing; the hooks do no work while disabled. Capability-based SOURCE: the dragon WebCodecs
  // path has no <video>, so its stats come from the active PlaybackPlayer (useCanvasInfo); every other
  // product reads its <video> (useVideoInfo). Both hooks are called unconditionally (rules-of-hooks);
  // only the matching one is enabled. Profile = what the View asked pbserver for (1 native / 2 down-res).
  const infoProfile = streamCtx.profile || 1;
  const videoInfo = useVideoInfo(videoRef, { enabled: showVideoInfo && hasClip && !isWebCodecs, profile: infoProfile });
  const canvasInfo = useCanvasInfo(wc.getActivePlayer, { enabled: showVideoInfo && hasClip && isWebCodecs, profile: requestedProfile });
  const activeInfo = isWebCodecs ? canvasInfo : videoInfo;
  const streamInfo = activeInfo ? formatVideoInfo(activeInfo) : null;

  // Per-cell OWN timestamp (David, ALL product modes): this cell shows the wall-clock time of the frame
  // IT is displaying = its active clip's start (sTimestamp) + the player's REAL in-clip position — NOT
  // the shared master-playhead time, which is identical in every corner and so MASKS desync across cams.
  // Capability-based source, same split as the info overlay: dragon reads the active PlaybackPlayer's
  // playheadSec; every other product reads its <video>.currentTime. A gap cell (no clip) has no own
  // position → falls back to the shared playhead time so the label still reads the timeline position.
  const getActivePlayer = wc.getActivePlayer;
  const getCellPosition = useCallback(() => {
    if (isWebCodecs) { const p = getActivePlayer && getActivePlayer(); return p ? p.playheadSec : null; }
    const v = videoRef.current;
    return v ? v.currentTime : null;
  }, [isWebCodecs, getActivePlayer]);
  const cellClock = useCellClock(getCellPosition, {
    clipStartTs: activeClip ? activeClip.sTimestamp : '',
    hasClip,
  });
  const activeSei = seiFrame && activeClip && seiFrame.clipId === activeClip.bID
    ? seiFrame.metadata
    : null;
  const b2Clock = activeSei ? formatB2Timestamp(activeSei.time, activeSei.gmtoffset) : '';
  const displayClock = b2Clock || cellClock;
  const b2CameraName = activeSei && typeof activeSei.camname === 'string'
    ? activeSei.camname.trim()
    : '';
  const displayCamera = b2CameraName ? { ...camera, sName: b2CameraName } : camera;

  // ── Per-cell actions (2c) — status from the active clip + the ••• menu wired to this cell ──────
  const archived = !!(activeClip && activeClip.fArchive);
  const exported = !!(activeClip && activeClip.fExport);

  const toggleFlag = (flag, isOn, onLabel, offLabel) => {
    if (!activeClip || !flagEvent) return;
    const { bID } = activeClip;
    const serial = camera && camera.bSerial; // the slot carries the NVR serial (events do not)
    const deviceId = camera && camera.sDeviceID; // CLOUD: the clip's device (for /ve/flagevent scope)
    const value = !isOn;
    // Defer the success toast until the RPC resolves so a failure shows ONLY the error toast — not both
    // the optimistic green "success" AND the error toast at once (#86baz05kd). The flag still flips
    // optimistically in state; this only governs the toast.
    flagEvent({ bID, flag, value, serial, deviceId })
      .then((res) => {
        if (res && !res.ok) {
          if (pushToast) pushToast({ message: "Couldn't reach the server, change undone", tone: 'error' });
          return;
        }
        if (pushToast) pushToast({
          message: value ? onLabel : offLabel,
          tone: 'success',
          undoLabel: 'Undo',
          onUndo: () => flagEvent({ bID, flag, value: !value, serial, deviceId }),
        });
      })
      .catch(() => { if (pushToast) pushToast({ message: "Couldn't reach the server, change undone", tone: 'error' }); });
  };

  const handleAction = (action) => {
    if (action === 'snap') {
      // Dragon frames live on the active <canvas> (no <video>); every other product on the <video>.
      const frameEl = isWebCodecs ? (wc.getActiveCanvas && wc.getActiveCanvas()) : videoRef.current;
      const shot = downloadScreenshot(frameEl, { camName: (displayCamera && displayCamera.sName) || '', timestamp: displayClock });
      // "No frame to capture YET" is only honest when the picture simply has not arrived. A cross-origin
      // eView member frame taints the canvas, so the read-back can NEVER succeed — telling the user to
      // wait for it is misleading. Say which one it is. #86bb7phhh
      if (pushToast) {
        if (shot.ok) pushToast({ message: 'Snapshot saved', tone: 'success' });
        else if (shot.reason === 'cross-origin') pushToast({ message: "Can't snapshot a camera on another NVR — the browser blocks reading its video", tone: 'error' });
        else pushToast({ message: 'No frame to capture yet', tone: 'error' });
      }
    } else if (action === 'dl') {
      // Download the OSD-burned clip: downloadUrl carries &overlay=1&mp4=1 for local/eview (saved file
      // shows name/timestamp/POS), or dragon's raw engine_backend URL. Fall back to the element's current
      // src if downloadUrl isn't ready yet.
      const src = downloadUrl || (videoRef.current && videoRef.current.src);
      if (!src) { if (pushToast) pushToast({ message: 'No clip to download', tone: 'error' }); return; }
      if (startDownload) startDownload(src, { camName: (displayCamera && displayCamera.sName) || 'cam', timestamp: displayClock });
    } else if (action === 'archive') {
      toggleFlag(ARCHIVE_FLAG, archived, 'Added to archive', 'Removed from archive');
    } else if (action === 'export') {
      toggleFlag(EXPORT_FLAG, exported, 'Flagged for export', 'Export flag removed');
    }
  };

  // Single-view focus lives in the ••• menu at conf>1 (David 2026-06-20 — replaces the ⤢ button; a
  // double-tap on the frame still does the same). Leads the clip actions; label flips while zoomed.
  const singleViewItem = (camera && canZoom && onZoom)
    ? [{
      action: 'single',
      label: zoomed ? 'Show all cameras' : 'View single',
      title: zoomed ? 'Show all cameras' : 'View this camera only',
      path: zoomed ? SHOW_ALL_PATH : SINGLE_VIEW_PATH,
      // The explicit ••• "View single" always drills to single-cam (toggleZoom via onViewSingle) — it must
      // NOT inherit the conf-8 double-tap "promote to big slot" behavior (onZoom = handleCellZoom). Falls
      // back to onZoom for any caller that doesn't pass onViewSingle.
      onClick: onViewSingle || onZoom,
    }]
    : [];
  // #16: the four surfaced actions (snap/dl/archive/export) — the cell's quick-action ROW. Always built
  // when a cam is loaded; visibility (hover / tap / always-show toggle) is CSS. Archive/Export carry
  // `active` so the row's buttons fill + persist as the clip's status.
  //
  // `compact` (David 2026-08-03) = mobile-portrait at conf 8+, where the tiles are too small to carry the
  // row: it collapses EVERYTHING into the ••• menu (even when the cog "always-show" is on) so only the
  // single ••• icon sits on the tile. Larger screens ignore compact and keep the surfaced row.
  const allActionItems = deriveActionItems(activeClip, { canExport, isWebCodecs });
  const quickActions = (camera && !compact) ? allActionItems.filter((it) => it.primary) : [];
  // ••• menu: View single + the FULL action set — ALWAYS (David 2026-08-06). Even when the quick-action
  // row is pinned (always-show ON), the ••• still lists every action so nothing is only reachable one way;
  // the surfaced row is just quick-access on top. (Was: de-duped the primaries out when always-show was ON.)
  const menuActionItems = allActionItems;
  // "Remove from view" (David 2026-08-13) — drop this cam from the current view. It's a LAYOUT edit
  // (dirty-able + revertible), NOT a device/footage delete — hence "from view". Trails the clip actions.
  const removeItem = (camera && onRemoveFromView)
    ? [{
      action: 'remove',
      label: 'Remove from view',
      title: 'Remove this camera from the current view',
      path: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11H7v-2h10v2z',
      onClick: () => onRemoveFromView(),
    }]
    : [];
  // "Camera Info" toggle (David 2026-08-28) — flip the global cog "Camera Info overlay" (resolution /
  // profile / buffer) straight from the cell ••• menu; it's the same global toggle as the Options sheet,
  // so it affects EVERY cell. Label reflects the current state. Trails the clip actions.
  const cameraInfoItem = (camera && onToggleVideoInfo)
    ? [{
      action: 'caminfo',
      label: showVideoInfo ? 'Hide camera info' : 'Show camera info',
      title: 'Toggle the on-video resolution / profile / buffer overlay on all cells',
      path: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z',
      onClick: () => onToggleVideoInfo(),
    }]
    : [];
  const menuItems = camera
    ? [...singleViewItem, ...menuActionItems.map((it) => ({ ...it, onClick: () => handleAction(it.action) })), ...cameraInfoItem, ...removeItem]
    : [];

  return (
    <Cell
      camera={displayCamera}
      videoUrl={isWebCodecs ? '' : (videoFallbackUrl || videoUrl)}
      videoRef={videoRef}
      wcReady={isWebCodecs && hasEverPainted}
      livePreview={livePreview}
      mediaSurface={isWebCodecs ? (
        // Double-buffered: active canvas visible, warm canvas pre-decoding the next clip hidden.
        // Promotion just flips activeBuf → which canvas is visible (both stay mounted → the warm
        // player's decoded frame 0 is already on-screen the instant it's promoted).
        <>
          <canvas ref={wc.canvasARef} className={`mcpb-cell__video${wc.activeBuf === 'A' ? '' : ' mcpb-cell__video--warm'}`} />
          <canvas ref={wc.canvasBRef} className={`mcpb-cell__video${wc.activeBuf === 'B' ? '' : ' mcpb-cell__video--warm'}`} />
        </>
      ) : null}
      infoText={displayClock}
      streamInfo={streamInfo}
      gap={inGap && !isLoading}
      loading={isLoading}
      // While the shared buffer-gate holds (ANY sibling still loading after a seek), show the loading
      // indicator on EVERY clip-bearing cell — even one whose own frame is ready — over its still frame.
      // The whole grid is "still loading" until all cams are ready to play together (David 2026-07-17):
      // nothing plays until the gate releases, so nothing should look like it's about to. Gap cells (no
      // clip) keep their "No video at this time" overlay (nothing to load there).
      buffering={(isBuffering || caching || holding) && hasClip && !(isWebCodecs && hasEverPainted && !(holding && isBuffering))}
      bufferingLabel={caching ? (cacheProgress > 0 ? `Caching ${cacheProgress}%` : 'Caching…') : ''}
      loadError={isWebCodecs ? wc.error : cacheError}
      recovering={mediaState === 'recovering'}
      failed={mediaState === 'failed'}
      quickActions={quickActions}
      onQuickAction={handleAction}
      menuItems={menuItems}
      zoomed={zoomed}
      dimmed={dimmed}
      canZoom={canZoom}
      onZoom={onZoom}
      isEview={!!streamCtx.fEview}
      camLabel={camLabel}
      dragEnabled={dragEnabled}
      selected={selected}
      onSelect={onSelect}
      actionsShown={actionsShown}
      flash={flash}
      hovered={hovered}
      slotIndex={slotIndex}
      dragging={draggable.isDragging}
      dragTarget={droppable.isOver && !draggable.isDragging}
      setDndRef={setDndRef}
      dndListeners={draggable.listeners}
      dndAttributes={draggable.attributes}
      onAddCamera={onAddCamera}
      onChangeCamera={onChangeCamera}
      onMediaError={onMediaError}
    />
  );
};

export default SlotCell;
